import React, { useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import styles from "./GlobalSearchResults.module.scss";
import { globalSearchIndexes, grad1, grad2, leumitSearchClient } from "assets/constants";
import { getNameByKey } from "helpers";
import Text from "components/text/Text";
import {
  InstantSearch,
  Configure,
  InfiniteHits,
  useMenu,
  useStats,
  useInstantSearch,
} from "react-instantsearch";
import {
  SITE_INDEX_HE,
  /*searchClient,*/
} from "assets/constants";
import strings from "assets/strings";
import { Hit, BaseHit } from "instantsearch.js";
import { SiteSearchResult } from "pages/globalSearchResults/SiteSearchResult/SiteSearchResult";
import { SiteSearchType } from "types";
import CustomSearchBox from "components/inputs/customSearchbox/CustomSearchBox";
import getRouting from "./routing";
import SvgGenerator from "components/svgGenerator/SvgGenerator";

const routing = getRouting(SITE_INDEX_HE);

// Utility to validate query strings
// const isValidQuery = (query: string) => {
//   // Example validation: reject queries with special characters like !!!
//   const invalidPattern = /[^\w\s]/g; // Adjust the regex based on your needs
//   return query && !invalidPattern.test(query);
// };

function InfiniteHitsComponent() {
  return (
    <InfiniteHits
      classNames={{
        item: "ais-InfiniteHits-item",
        loadPrevious: "ais-PreviousResults",
        loadMore: "ais-InfiniteHits-loadMore",
      }}
      translations={{
        showMoreButtonText: strings.globalStrings.moreResults,
      }}
      hitComponent={({ hit }: { hit: Hit<BaseHit> }) => {
        return <SiteSearchResult hit={hit as SiteSearchType} />;
      }}
    />
  );
}

const HitsWrapper = () => {
  const { nbHits } = useStats();
  const { status, results } = useInstantSearch();
  const noResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status === 'idle' && nbHits === 0 && noResultsRef.current) {
      noResultsRef.current.focus();
    }
  }, [status, nbHits]);

  const extendedResults = results as typeof results & {
    __isError?: boolean;
    __errorStatus?: number;
  };

  if (extendedResults.__isError && extendedResults.__errorStatus === 403) {
    return (
      <div
        className={styles.noMessagesWrapper}>
        <Text text={strings.globalStrings.networkError} />
      </div>
    );
  }
  // No results
  if (status === 'idle' && nbHits === 0) {
    return (
      <div
        className="noMessagesWrapper"
        role="status"
        aria-live="assertive"
        tabIndex={-1}
        ref={noResultsRef}>
        <SvgGenerator
          name="Magnifier"
          className="magnifierIcon"
          objectName="medicalCenterSearchResults"
        />
        <Text text={strings.globalStrings.noResultsFound} />
      </div>
    );
  }

  // Results
  return (
    <>
      {nbHits > 0 && <InfiniteHitsComponent />}
    </>
  );
};


const Pills = forwardRef((_, ref: React.ForwardedRef<any>) => {
  const { items, refine } = useMenu({ attribute: "objectType", limit: 15, sortBy: ["count:desc"] });
  const [totalHits, setTotalHits] = React.useState<number>(0);
  const [currentPill, setCurrentPill] = React.useState<string>("");

  useImperativeHandle(ref, () => ({
    handleSelectedPill,
  }));

  useEffect(() => {
    setTotalHits(items.reduce((acc, item) => acc + item.count, 0));
  }, [items]);

  const handleSelectedPill = (refiner: string) => {
    refine(refiner);
    setCurrentPill(refiner);
  };

  return totalHits ? (
    <div className={styles.searchpills}>
      <ul>
        <li>
          <a
            onClick={() => (currentPill === "" ? false : handleSelectedPill(""))}
            className={currentPill === "" ? styles.active : ""}
          >
            {`${strings.globalSearchResults.allResults} (${totalHits})`}
          </a>
        </li>
        {items &&
          items.map((refiner) => (
            <li key={refiner.label}>
              <a
                onClick={() => (refiner.isRefined ? false : handleSelectedPill(refiner.label))}
                className={refiner.isRefined ? styles.active : ""}
              >
                {`${getNameByKey(refiner.label, globalSearchIndexes)} (${refiner.count})`}
              </a>
            </li>
          ))}
      </ul>
    </div>
  ) : null;
});

export const GlobalSearchResults = () => {
  const pillsRef = useRef<any>(null);

  // Custom handler for intercepting invalid queries
  // const handleStateChange = ({ uiState }: any) => {
  //   if (uiState[SITE_INDEX_HE]?.query && !isValidQuery(uiState[SITE_INDEX_HE].query)) {
  //     // Clear the query if it's invalid
  //     uiState[SITE_INDEX_HE].query = "";
  //   }
  // };

  return (
    <InstantSearch
      indexName={SITE_INDEX_HE}
      searchClient={leumitSearchClient}
      routing={routing}
    //onStateChange={handleStateChange}
    >
      <Configure hitsPerPage={10} />
      <div className={styles.searchHeader}>
        <CustomSearchBox
          enableOnlyAlphanumeric
          styleInput={{ marginInlineStart: ".8rem" }}
          useStyleButton
          color1={grad1}
          color2={grad2}
          btnText={strings.globalStrings.search}
          label={strings.globalStrings.search}
          className={styles.globalSearch}
          isAutoComplete
          indexName={SITE_INDEX_HE}
        />
      </div>
      <Pills ref={pillsRef} />
      <HitsWrapper />
    </InstantSearch>
  );
};
